import React, { useMemo } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  CircularProgress,
} from '@mui/material';
import {
  CloudQueue as CloudIcon,
  PlayCircleOutline as PlayIcon,
} from '@mui/icons-material';
import { colors } from '../theme/expertiseTheme';
import {
  CONTA_SERVICO_DRIVE,
  EXTENSOES_AUDIO_ACEITAS,
  MAX_JOBS_SIMULTANEOS,
  MAX_LINKS_POR_LOTE,
  extrairLinks,
  isGoogleDriveFolderUrl,
} from '../services/transcricao';

const textoCompartilhamento = CONTA_SERVICO_DRIVE
  ? `As pastas precisam estar compartilhadas com ${CONTA_SERVICO_DRIVE} (ou públicas com link).`
  : 'As pastas precisam estar compartilhadas com a conta de serviço da Expertise (ou públicas com link).';

/**
 * Tela inicial da transcrição: vincular uma ou mais pastas do Google Drive.
 * Até MAX_JOBS_SIMULTANEOS pastas são processadas ao mesmo tempo; as demais entram na fila.
 */
type Props = {
  links: string;
  onChange: (valor: string) => void;
  onIniciar: () => void;
  loading: boolean;
  erroCampo?: string;
  /** Versão reduzida, exibida abaixo da lista de jobs em andamento */
  compacto?: boolean;
};

const passos = [
  {
    numero: 1,
    titulo: 'Cole os Links',
    descricao: 'Copie os links de compartilhamento das pastas do Google Drive com seus áudios, um por linha.',
  },
  {
    numero: 2,
    titulo: 'Processamento Automático',
    descricao: `Até ${MAX_JOBS_SIMULTANEOS} pastas são transcritas ao mesmo tempo; as outras aguardam na fila e começam sozinhas.`,
  },
  {
    numero: 3,
    titulo: 'Exporte Relatórios',
    descricao: 'Baixe as transcrições individuais ou um relatório consolidado de cada pasta.',
  },
];

/** Resumo do que foi colado: quantos links válidos e inválidos */
export const resumirLinks = (texto: string) => {
  const links = extrairLinks(texto);
  const validos = links.filter(isGoogleDriveFolderUrl);
  return { total: links.length, validos: validos.length, invalidos: links.length - validos.length };
};

const TranscricaoVincularPasta: React.FC<Props> = ({
  links,
  onChange,
  onIniciar,
  loading,
  erroCampo,
  compacto = false,
}) => {
  const resumo = useMemo(() => resumirLinks(links), [links]);

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    onIniciar();
  };

  const helper = (() => {
    if (erroCampo) return erroCampo;
    if (resumo.total === 0) return textoCompartilhamento;
    const partes = [`${resumo.validos} pasta${resumo.validos === 1 ? '' : 's'} para transcrever`];
    if (resumo.invalidos > 0) partes.push(`${resumo.invalidos} link${resumo.invalidos === 1 ? '' : 's'} inválido${resumo.invalidos === 1 ? '' : 's'}`);
    if (resumo.validos > MAX_JOBS_SIMULTANEOS) {
      partes.push(`${resumo.validos - MAX_JOBS_SIMULTANEOS} aguarda${resumo.validos - MAX_JOBS_SIMULTANEOS === 1 ? '' : 'm'} na fila`);
    }
    return partes.join(' · ');
  })();

  const rotuloBotao = loading
    ? 'Enviando...'
    : resumo.validos > 1
      ? `Transcrever ${resumo.validos} pastas`
      : 'Iniciar Transcrição Automática';

  const formulario = (
    <Box component="form" onSubmit={handleSubmit} sx={{ maxWidth: 640, mx: 'auto' }}>
      <TextField
        fullWidth
        multiline
        minRows={compacto ? 2 : 3}
        maxRows={10}
        value={links}
        onChange={(event) => onChange(event.target.value)}
        placeholder={'https://drive.google.com/drive/folders/1A2b3C4d5E6f...\nhttps://drive.google.com/drive/folders/9Z8y7X6w5V4u...'}
        disabled={loading}
        error={Boolean(erroCampo)}
        helperText={helper}
        inputProps={{ 'aria-label': 'Links das pastas do Google Drive' }}
        sx={{
          '& .MuiOutlinedInput-root': {
            py: 0.75,
            fontSize: '0.9375rem',
            fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
          },
          '& .MuiFormHelperText-root': {
            textAlign: 'center',
            mt: 1.5,
            fontSize: '0.8125rem',
          },
        }}
      />

      <Button
        type="submit"
        variant="contained"
        color="primary"
        fullWidth
        disabled={loading || resumo.total > MAX_LINKS_POR_LOTE}
        startIcon={
          loading ? <CircularProgress size={18} sx={{ color: colors.white }} /> : <PlayIcon />
        }
        sx={{
          mt: 3,
          py: compacto ? 1.25 : 1.75,
          fontSize: compacto ? '0.9375rem' : '1rem',
          borderRadius: '10px',
        }}
      >
        {rotuloBotao}
      </Button>
      {resumo.total > MAX_LINKS_POR_LOTE && (
        <Typography variant="caption" sx={{ color: colors.error, display: 'block', mt: 1, textAlign: 'center' }}>
          Envie no máximo {MAX_LINKS_POR_LOTE} links por vez.
        </Typography>
      )}
    </Box>
  );

  if (compacto) {
    return (
      <Paper
        elevation={0}
        sx={{ p: { xs: 3, md: 4 }, border: `1px solid ${colors.border}`, borderRadius: '16px' }}
      >
        <Typography variant="h6" sx={{ fontWeight: 600, color: colors.dark, mb: 0.5, textAlign: 'center' }}>
          Adicionar mais pastas
        </Typography>
        <Typography variant="body2" sx={{ color: colors.text.secondary, mb: 3, textAlign: 'center' }}>
          Novas pastas entram no fim da fila e começam assim que houver vaga.
        </Typography>
        {formulario}
      </Paper>
    );
  }

  return (
    <Box sx={{ maxWidth: 920, mx: 'auto' }}>
      {/* Card principal */}
      <Paper
        elevation={0}
        sx={{
          p: { xs: 3, md: 6 },
          border: `1px solid ${colors.border}`,
          borderRadius: '16px',
          textAlign: 'center',
        }}
      >
        <Box
          sx={{
            width: 72,
            height: 72,
            borderRadius: '18px',
            backgroundColor: 'rgba(0, 99, 0, 0.1)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mx: 'auto',
            mb: 3,
          }}
        >
          <CloudIcon sx={{ fontSize: 36, color: colors.primary }} />
        </Box>

        <Typography
          variant="h4"
          sx={{
            fontWeight: 700,
            color: colors.dark,
            fontSize: { xs: '1.5rem', md: '1.875rem' },
            mb: 1,
          }}
        >
          Vincular Pasta do Google Drive
        </Typography>

        <Typography variant="body2" sx={{ color: colors.text.secondary, mb: 4 }}>
          Cole um ou mais links de pastas contendo os arquivos de áudio ({EXTENSOES_AUDIO_ACEITAS}),
          um por linha
        </Typography>

        {formulario}
      </Paper>

      {/* Como funciona */}
      <Box sx={{ mt: 6 }}>
        <Typography
          variant="h6"
          sx={{ fontWeight: 600, color: colors.dark, textAlign: 'center', mb: 3 }}
        >
          Como funciona?
        </Typography>

        <Grid container spacing={3}>
          {passos.map((passo) => (
            <Grid item xs={12} md={4} key={passo.numero}>
              <Paper
                elevation={0}
                sx={{ height: '100%', p: 3, border: `1px solid ${colors.border}`, borderRadius: '16px' }}
              >
                <Box
                  sx={{
                    width: 36,
                    height: 36,
                    borderRadius: '10px',
                    backgroundColor: 'rgba(0, 99, 0, 0.1)',
                    color: colors.primary,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontWeight: 700,
                    fontSize: '0.9375rem',
                    mb: 2,
                  }}
                >
                  {passo.numero}
                </Box>

                <Typography variant="subtitle1" sx={{ fontWeight: 600, color: colors.dark, mb: 1 }}>
                  {passo.titulo}
                </Typography>

                <Typography variant="body2" sx={{ color: colors.text.secondary }}>
                  {passo.descricao}
                </Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Box>
    </Box>
  );
};

export default TranscricaoVincularPasta;
